import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { Search, SlidersHorizontal, X, Info, Link2, Code, FileText, ChevronLeft, ChevronRight, ChevronsUpDown } from 'lucide-react';
import toast from 'react-hot-toast';
import '../styles/EventLogViewer.css';

const EventLogViewer = ({ user }) => {
    const [logs, setLogs] = useState([]);
    const [properties, setProperties] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isEmployeeListLoading, setIsEmployeeListLoading] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalLogs: 0 });
    const [sortConfig, setSortConfig] = useState({ key: 'punch_time', direction: 'DESC' });

    const [filters, setFilters] = useState({
        propertyId: '',
        employeeCode: '',
        deviceSn: '',
        status: '',
        startDate: new Date(new Date().setDate(new Date().getDate() - 6)).toISOString().slice(0, 10),
        endDate: new Date().toISOString().slice(0, 10),
    });

    const fetchLogs = useCallback(async (page = 1) => {
        setIsLoading(true);
        try {
            const validFilters = {
                ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v != null && v !== '')),
                page: page,
                sortBy: sortConfig.key,
                sortOrder: sortConfig.direction,
            };
            const { logs: data, pagination: newPagination } = await api.getDiagnosticEvents(validFilters);
            setLogs(data);
            setPagination(newPagination);
        } catch (err) {
            toast.error(`Failed to fetch logs: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    }, [filters, sortConfig]);

    useEffect(() => {
        fetchLogs(pagination.currentPage);
    }, [pagination.currentPage, fetchLogs]);
    
    const isInitialMount = useRef(true);
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        const searchTimer = setTimeout(() => {
            handleSearch();
        }, 500);

        return () => clearTimeout(searchTimer);
    }, [filters]);

    useEffect(() => {
        const loadProperties = async () => {
            try {
                const props = await api.getProperties();
                setProperties(props);
            } catch (err) {
                toast.error(`Failed to load properties: ${err.message}`);
            }
        };
        loadProperties();
    }, []);

    useEffect(() => {
        const fetchEmployeesForProperty = async () => {
            setEmployees([]);
            setFilters(prev => ({ ...prev, employeeCode: '' }));
            if (!filters.propertyId) return;

            setIsEmployeeListLoading(true);
            try {
                const workforce = await api.getWorkforce({ propertyId: filters.propertyId });
                const punchableEmployees = workforce.filter(u => u.employee_code);
                setEmployees(punchableEmployees);
            } catch (err) {
                toast.error(`Could not load employees: ${err.message}`);
            } finally {
                setIsEmployeeListLoading(false);
            }
        };
        fetchEmployeesForProperty();
    }, [filters.propertyId]);
    
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSearch = () => {
        if (pagination.currentPage !== 1) {
            setPagination(prev => ({ ...prev, currentPage: 1 }));
        } else {
            fetchLogs(1);
        }
    };
    
    const handleResetFilters = () => {
        setFilters({
            propertyId: '', employeeCode: '', deviceSn: '', status: '',
            startDate: new Date(new Date().setDate(new Date().getDate() - 6)).toISOString().slice(0, 10),
            endDate: new Date().toISOString().slice(0, 10),
        });
        if (pagination.currentPage !== 1) {
            setPagination(prev => ({ ...prev, currentPage: 1 }));
        } else {
            fetchLogs(1);
        }
    };
    
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages && !isLoading) {
            setPagination(prev => ({ ...prev, currentPage: newPage }));
        }
    };

    const handleSort = (key) => {
        let direction = 'ASC';
        if (sortConfig.key === key && sortConfig.direction === 'ASC') {
            direction = 'DESC';
        }
        setSortConfig({ key, direction });
        // After setting sort, go to page 1 to see the new order from the start
        if (pagination.currentPage !== 1) {
            setPagination(prev => ({ ...prev, currentPage: 1 }));
        } else {
            // If already on page 1, we need to trigger a refetch
            fetchLogs(1);
        }
    };

    const getSortClassName = (name) => {
        if (sortConfig.key !== name) {
            return 'sortable';
        }
        return sortConfig.direction === 'ASC' ? 'sortable sorted-asc' : 'sortable sorted-desc';
    };

    const openModal = (log) => { setSelectedLog(log); setActiveTab('overview'); };
    const closeModal = () => { setSelectedLog(null); };

    const renderStatusIcon = (log) => {
        if (log.processed_at) {
            if (log.processing_comments && !log.processing_comments.toLowerCase().includes('success')) {
                return <span title={log.processing_comments} className="status-icon warning">⚠️</span>;
            }
            return <span title="Processed" className="status-icon success">✅</span>;
        }
        return <span title="Unprocessed" className="status-icon error">❌</span>;
    };

    return (
        <div className="page-container event-log-viewer">
            <div className="page-header">
                <h1 className="page-title">Device Event Log Viewer</h1>
            </div>

            <div className="card filter-card">
                 <div className="filter-bar-header">
                    <SlidersHorizontal size={18} />
                    <h3>Filters</h3>
                </div>
                <div className="log-filters">
                    <div className="filter-group date-range-group">
                        <label>Date Range</label>
                        <div className="date-inputs">
                            <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} className="form-input" />
                            <span>to</span>
                            <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} className="form-input" />
                        </div>
                    </div>
                     <div className="filter-group">
                        <label>Property</label>
                        <select name="propertyId" value={filters.propertyId} onChange={handleFilterChange} className="form-input">
                            <option value="">All Properties</option>
                            {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>Employee</label>
                        <select 
                            name="employeeCode" 
                            value={filters.employeeCode} 
                            onChange={handleFilterChange} 
                            className="form-input"
                            disabled={!filters.propertyId || isEmployeeListLoading}
                        >
                            <option value="">
                                {isEmployeeListLoading 
                                    ? 'Loading...' 
                                    : !filters.propertyId 
                                        ? 'Select a property first' 
                                        : 'All Employees'}
                            </option>
                            {employees.map(emp => (
                                <option key={emp.id} value={emp.employee_code}>
                                    {emp.first_name} {emp.last_name} ({emp.employee_code})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-group">
                        <label>Processing Status</label>
                        <select name="status" value={filters.status} onChange={handleFilterChange} className="form-input">
                            <option value="">All</option>
                            <option value="processed">Processed Successfully</option>
                            <option value="unprocessed">Unprocessed</option>
                            <option value="with_comments">With Comments/Warnings</option>
                        </select>
                    </div>
                    <div className="filter-actions">
                        <button onClick={handleResetFilters} className="button button-secondary">Reset</button>
                        <button onClick={handleSearch} className="action-button" disabled={isLoading}>
                            <Search size={16} /> {isLoading ? 'Searching...' : 'Search'}
                        </button>
                    </div>
                </div>
            </div>

            <div className="card report-container">
                <div className="pagination-controls">
                    <span>
                        Page <strong>{pagination.currentPage}</strong> of <strong>{pagination.totalPages}</strong> ({pagination.totalLogs} total logs)
                    </span>
                    <div className="pagination-buttons">
                        <button onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={isLoading || pagination.currentPage <= 1}>
                            <ChevronLeft size={16} /> Previous
                        </button>
                        <button onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={isLoading || pagination.currentPage >= pagination.totalPages}>
                            Next <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
                
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>
                                <div onClick={() => handleSort('punch_time')} className={getSortClassName('punch_time')}>
                                    <span>Timestamp</span>
                                    <ChevronsUpDown size={14} />
                                </div>
                            </th>
                            <th>
                                <div onClick={() => handleSort('device_sn')} className={getSortClassName('device_sn')}>
                                    <span>Device SN</span>
                                    <ChevronsUpDown size={14} />
                                </div>
                            </th>
                             <th>
                                <div onClick={() => handleSort('property_name')} className={getSortClassName('property_name')}>
                                    <span>Property</span>
                                    <ChevronsUpDown size={14} />
                                </div>
                            </th>
                            <th>
                                <div onClick={() => handleSort('employee_code')} className={getSortClassName('employee_code')}>
                                    <span>Employee</span>
                                    <ChevronsUpDown size={14} />
                                </div>
                            </th>
                            <th>Event</th>
                            <th style={{ textAlign: 'center' }}>Status</th>
                            <th>Traceability</th>
                            <th>Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan="8" className="placeholder-text">Loading logs...</td></tr>
                        ) : logs.length > 0 ? (
                            logs.map(log => (
                                <tr key={log.event_id}>
                                    <td>{new Date(log.punch_time).toLocaleString()}</td>
                                    <td>{log.device_sn}</td>
                                    <td>{log.property_name || 'N/A'}</td>
                                    <td>{log.employee_code || 'N/A'}</td>
                                    <td>{log.event_type}: {log.action_type}</td>
                                    <td style={{ textAlign: 'center' }}>{renderStatusIcon(log)}</td>
                                    <td>
                                        {log.punch_id && <span className="trace-link"><Link2 size={12} /> Punch: {log.punch_id}</span>}
                                        {log.time_entry_start_id && <span className="trace-link"><Link2 size={12} /> TE Start: {log.time_entry_start_id}</span>}
                                        {log.time_entry_end_id && <span className="trace-link"><Link2 size={12} /> TE End: {log.time_entry_end_id}</span>}
                                    </td>
                                    <td>
                                        <button onClick={() => openModal(log)} className="action-button-small">View</button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan="8" className="placeholder-text">No logs found for the selected filters.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {selectedLog && (
                <div className="modal-backdrop">
                    <div className="modal-content log-modal">
                        <div className="modal-header">
                            <h2>Event Details (ID: {selectedLog.event_id})</h2>
                            <button onClick={closeModal} className="icon-button"><X size={20} /></button>
                        </div>
                        <div className="modal-body">
                            <div className="detail-tabs">
                                <button className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}><Info size={16} /> Overview</button>
                                <button className={`tab-button ${activeTab === 'raw' ? 'active' : ''}`} onClick={() => setActiveTab('raw')}><Code size={16} /> Raw Data</button>
                            </div>
                            <div className="tab-content">
                                {activeTab === 'overview' && (
                                    <div className="detail-grid">
                                        <div className="detail-label">Timestamp</div><div className="detail-value">{new Date(selectedLog.punch_time).toLocaleString()}</div>
                                        <div className="detail-label">Device SN</div><div className="detail-value">{selectedLog.device_sn}</div>
                                        <div className="detail-label">Employee Code</div><div className="detail-value">{selectedLog.employee_code}</div>
                                        <div className="detail-label">Event Type</div><div className="detail-value">{selectedLog.event_type}</div>
                                        <div className="detail-label">Action Type</div><div className="detail-value">{selectedLog.action_type}</div>
                                        <div className="detail-label">Processing Comments</div><div className="detail-value processing-comments">{selectedLog.processing_comments || 'N/A'}</div>
                                    </div>
                                )}
                                {activeTab === 'raw' && (
                                    <div className="raw-data-viewer">
                                        <h4><FileText size={14}/> Raw Event Data</h4>
                                        <pre><code>{selectedLog.raw_event_data}</code></pre>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventLogViewer;